<template>
  <div>
    <slot name="out-area"></slot>
    <div :class="`wrapper-list ${wrapperClass}`">
      <b-row :class="`margin-0 ${headerClass}`">
        <b-col v-if="hasTitle" cols="12" :class="`px-0 ${insideHeaderClass}`">
          <div :class="`wrapper-list__title flex-start ${titleClass}`">
            <slot name="back-button-area">
              <Button
                v-if="hasBackButton"
                variant="outline-complementary"
                :justIcon="backButtonIcon"
                contentClass="button__just-icon"
                class="margin-left-16"
                @click="handleBackButton"
              />
            </slot>
            <span class="wrapper-list__title-text">{{ pageTitle }}</span>
            <span
              v-if="!loading && paging.total"
              class="wrapper-list__title-count"
            >
              <span class="color-complementary-100 margin-x-4">|</span>
              <span>{{ paging.total }}</span>
            </span>
          </div>
        </b-col>
        <b-col
          cols="12"
          :class="`desktop-flex-between padding-0 ${menuBarClass} ${
            !showSearchTxt ? 'flex-between' : ''
          }`"
        >
          <div
            :class="`desktop-flex-start position-relative ${
              !showSearchTxt ? 'flex-start' : ''
            } ${searchTxtClass}`"
          >
            <div
              v-if="movingButtons.length && (!showSearchTxt || !isUseInSidebar)"
              :class="{ 'mobile-none tablet-none': showSearchTxt }"
            >
              <Button
                variant="outline-complementary"
                justIcon="dots-vertical"
                contentClass="button__just-icon"
                class="margin-left-8"
                @click="handleOpenMovingButtons"
              />
              <span>
                <MenuPopup
                  :show="isMovingButtons"
                  :items="movingButtons"
                  :permissions="permissions"
                  class="right-0 top-25 z-index-2 padding-top-24"
                  @close="handleCloseMovingButtons"
                />
              </span>
            </div>
            <div
              v-if="!hasLazyLoadingPagination"
              :class="{ 'mobile-none tablet-none': showSearchTxt }"
            >
              <div class="margin-left-8 width-72">
                <Dropdown
                  :value="form.perPage"
                  :options="perPageOptions"
                  name="perPage"
                  id="perPage"
                  inputPlaceholder=""
                  rowClass=""
                  :hasRemoveIcon="false"
                  @input="input"
                  @change="fetchData"
                />
              </div>
            </div>
            <div
              v-if="!!textFilters.length"
              :class="{ 'width-percent-100': showSearchTxt && isUseInSidebar }"
            >
              <SearchInput
                v-if="showSearchTxt || isSearchTxtParam"
                :value="form.searchTxt"
                name="searchTxt"
                id="searchTxt"
                type="text"
                :placeholder="pathOr('', ['0', 'search_scope'], textFilters)"
                :vuelidateObj="$v.form.searchTxt"
                :hasEraseIcon="true"
                :autoFocus="true"
                :widthDropDown="true"
                rightIcon="magnify"
                @input="input"
                @handleSubmit="handleSubmitSearchText"
                @close="closeSearchText"
                @erase="eraseSearchText"
                @removeFilter="removeFilter"
              />
              <Button
                v-else
                variant="outline-complementary"
                justIcon="magnify"
                contentClass="button__just-icon"
                @click="showSearchTxt = true"
              />
            </div>
            <div
              v-if="
                !!sidebarFilters.length && (!showSearchTxt || !isUseInSidebar)
              "
              :class="{ 'mobile-none tablet-none': showSearchTxt }"
            >
              <Button
                variant="outline-complementary"
                justIcon="filter-variant"
                contentClass="button__just-icon"
                class="desktop-none margin-right-8"
                @click="showFilter"
              />
              <Button
                variant="outline-complementary"
                rightIcon="filter-variant"
                :text="$lang.add_filter"
                class="mobile-none tablet-none margin-right-8 width--max-content"
                @click="showFilter"
              />
            </div>
          </div>
          <FilterHistory
            v-if="filterHistoryItems.length && filterHistoryType === 'DESKTOP'"
            class="mobile-none tablet-none"
            :filters="filterHistoryItems"
            :textFilters="textFilters"
            :removeFilter="removeFilter"
            :mode="filterHistoryType"
            @showFilter="handleOpenFilters"
          />
          <div
            v-if="
              hasAddButton &&
              checkPermission(permissions, ['admin', 'edit']) &&
              (!showSearchTxt || !isUseInSidebar)
            "
            :class="{ 'mobile-none tablet-none': showSearchTxt }"
          >
            <Button
              variant="primary"
              justIcon="plus"
              contentClass="button__just-icon"
              class="desktop-none"
              @click="handleAddButton"
            />
            <Button
              variant="primary"
              rightIcon="plus"
              :text="addButtonText"
              class="mobile-none tablet-none"
              @click="handleAddButton"
            />
          </div>
        </b-col>
        <b-col
          v-if="filterHistoryItems.length || filterHistoryType === 'MOBILE'"
          cols="12"
          class="margin-top-16 px-0"
          :class="{ 'desktop-none': filterHistoryType !== 'MOBILE' }"
        >
          <FilterHistory
            :filters="filterHistoryItems"
            :textFilters="textFilters"
            :removeFilter="removeFilter"
            :mode="'MOBILE'"
            @showFilter="handleOpenFilters"
          />
        </b-col>
        <b-col cols="12">
          <slot name="top-area"></slot>
        </b-col>
      </b-row>
      <LazyLoadingWrapper
        :wrapperClass="`wrapper-list__content ${wrapperListContentClass}`"
        :style="wrapperListContentStyle"
        :loading="loadMoreLoading"
        :loadMore="loadMore"
        :emptyImage="generateEmptyImage"
        :emptyText="generateEmptyText"
        :isEmpty="isEmpty"
        scrollDistance="2000"
      >
        <slot name="default" :items="items" :itemsLoading="loading"></slot>
        <slot name="empty-box" v-if="isEmpty">
          <template slot="empty-box">
            <b-img-lazy
              class="empty-box__image width-181 margin-bottom-21"
              :src="generateEmptyImage"
            />
            <div class="empty-box__text color-complementary-300 font-size-14">
              {{ generateEmptyText }}
            </div>
          </template>
        </slot>
      </LazyLoadingWrapper>
      <FilterWrapper
        :visible="filterVisible"
        :filters="sidebarFilters"
        :params="params"
        :exceptParams="exceptParams"
        :loading="filterLoading"
        :parentCurrentFilterKey="parentCurrentFilterKey"
        @submit="submitFilter"
        @cancelAll="deleteFilters"
        @close="closeFilter"
      />
    </div>
  </div>
</template>

<script>
import inputMixin from "@/mixin/inputMixin.vue";
import filterCacheMixinVue from "@/mixin/filterCacheMixin.vue";
import FilterWrapper from "@/components/re-design/filters/FilterWrapper.vue";
import FilterHistory from "@/components/re-design/filters/FilterHistory.vue";
import { Dropdown, Button, SearchInput } from "@/components/re-design/forms";
import MenuPopup from "@/components/re-design/modal/MenuPopup.vue";
import LazyLoadingWrapper from "@/components/re-design/lazy-loading-wrapper/LazyLoadingWrapper.vue";
import infiniteScroll from "vue-infinite-scroll";
import { required } from "vuelidate/lib/validators";
import { pathOr, includes } from "ramda";
import { computed } from "vue";
import {
  checkPermission,
  generateError,
  generateQueryString,
  storeDispatcher,
} from "@/shared/utils";
import { exportExcel } from "@/services";

export default {
  mixins: [inputMixin, filterCacheMixinVue],
  components: {
    FilterWrapper,
    FilterHistory,
    Dropdown,
    Button,
    SearchInput,
    MenuPopup,
    LazyLoadingWrapper,
  },
  provide() {
    // provide this data to all deep child components
    return {
      HEADER_LOADING: computed(() => this.loading),
      listWrapperReload: this.reload,
    };
  },
  props: {
    // type of this component for display show data: CARD_LIST, TABLE
    displayType: {
      type: String,
      default: "CARD_LIST",
    },
    url: {
      type: String,
      default: null,
    },
    insideHeaderClass: {
      type: String,
      defualt: "mobile-none tablet-none",
    },
    // inject more buttons of parent to top buttons
    extraButtons: {
      type: Array,
      default: () => [],
    },
    otherExportExcelParams: {
      type: Object,
      default: null,
    },
    hasExportExcel: {
      type: Boolean,
      default: true,
    },
    hasLazyLoadingPagination: {
      type: Boolean,
      default: true,
    },
    addButtonText: {
      type: String,
      default: "",
    },
    extraParams: {
      type: Object,
      default: null,
    },
    title: {
      type: String,
      default: "",
    },
    emptyImage: {
      type: String,
      default: "",
    },
    emptyText: {
      type: String,
      default: "",
    },
    defaultPerPage: {
      type: Number,
      default: 20,
    },
    hasTitle: {
      type: Boolean,
      default: true,
    },
    hasAddButton: {
      type: Boolean,
      default: true,
    },
    wrapperClass: {
      type: String,
      default: "",
    },
    headerClass: {
      type: String,
      default: "margin-bottom-16",
    },
    titleClass: {
      type: String,
      default: "",
    },
    menuBarClass: {
      type: String,
      default: "",
    },
    isUseInSidebar: {
      type: Boolean,
      default: false,
    },
    searchTxtClass: {
      type: String,
      default: "desktop-width-max-content",
    },
    isNewStructure: {
      type: Boolean,
      default: false,
    },
    wrapperListContentClass: {
      type: String,
      default: "",
    },
    wrapperListContentStyle: {
      type: String,
      default: "",
    },
    hasBackButton: {
      type: Boolean,
      default: false,
    },
    backButtonIcon: {
      type: String,
      default: "close",
    },
    filterHistoryType: {
      type: String,
      default: "DESKTOP",
    },
  },
  validations() {
    return {
      form: {
        perPage: {},
        searchTxt: {},
      },
    };
  },
  data() {
    return {
      form: {
        perPage: {
          id: this.defaultPerPage,
          caption: String(this.defaultPerPage),
        },
        searchTxt: null,
      },
      perPageOptions: [
        {
          id: 10,
          caption: "10",
        },
        {
          id: 20,
          caption: "20",
        },
        {
          id: 30,
          caption: "30",
        },
        {
          id: 40,
          caption: "40",
        },
        {
          id: 50,
          caption: "50",
        },
        {
          id: 60,
          caption: "60",
        },
        {
          id: 70,
          caption: "70",
        },
        {
          id: 80,
          caption: "80",
        },
        {
          id: 90,
          caption: "90",
        },
        {
          id: 100,
          caption: "100",
        },
      ],
      showSearchTxt: false,
      isMovingButtons: false,
      firstLoaded: false,
      loading: false,
      loadMoreLoading: false,
      exportExcelLoading: false,
      items: [],
      filters: [],
      permissions: [],
      others: null,
      resource: null,
      exportable: false,
      paging: {
        total: 0,
        count: 0,
        totalPages: 1,
        currentPage: 1,
        perPage: this.defaultPerPage,
      },
      debounceGet: null,
      debounceTime: 800,
      filterVisible: false,
      filterLoading: false,
      searchKey: null,
      params: {
        page: 1,
        per_page: this.defaultPerPage,
      },
      defaultParams: {
        page: 1,
        per_page: this.defaultPerPage,
      },
      parentCurrentFilterKey: null,
    };
  },
  computed: {
    movingButtons() {
      let buttons = [
        ...this.extraButtons,
        {
          icon: "table-cog",
          caption: this.$lang.setting,
          show: this.displayType === "TABLE",
          permissions: ["admin", "edit", "view"],
          click: () => {},
        },
        {
          icon: "microsoft-excel",
          caption: this.$lang.excel_export,
          show: this.hasExportExcel && this.exportable,
          permissions: ["admin", "edit"],
          loading: this.exportExcelLoading,
          click: this.handleExportExcel,
        },
      ];
      // filtering buttons with show key
      buttons = buttons.filter((button) => button.show);

      // filtering buttons with permissions
      buttons = buttons.filter((button) =>
        checkPermission(this.permissions, button.permissions)
      );

      return buttons;
    },
    pageTitle() {
      return this.title || this.$route.meta.label;
    },
    textFilters() {
      return this.filters.filter(
        ({ key_service }) => key_service === "search_txt"
      );
    },
    sidebarFilters() {
      return this.filters.filter(
        ({ key_service }) => key_service !== "search_txt"
      );
    },
    filterHistoryItems() {
      const selectedFilters = Object.keys(this.params).filter(
        (item) => !includes(item, this.exceptParams)
      );

      return this.filters
        .filter((item) => includes(item.key_service, selectedFilters))
        .map((item) => {
          return {
            id: item.key_service,
            caption: item.key_filter_caption,
            type: item.type,
            value: this.params[item.key_service],
          };
        });
    },
    generateEmptyImage() {
      return this.emptyImage || this.$config.images.shared.redesign.empty.list;
    },
    generateEmptyText() {
      return this.emptyText || this.$lang.list_empty_text;
    },
    isEmpty() {
      return this.firstLoaded && !this.items.length && !this.loading;
    },
    isSearchTxtParam() {
      const paramsKey = Object.keys(this.params);
      return includes("search_txt", paramsKey);
    },
  },
  directives: { infiniteScroll },
  watch: {
    url: {
      immediate: true,
      async handler(value) {
        if (value) {
          this.reload();
        }
      },
    },
    extraParams: {
      immediate: true,
      async handler(value) {
        if (value) {
          // this.reload();
        }
      },
    },
    loading: {
      immediate: true,
      handler(value) {
        this.$emit("getLoading", value);
      },
    },
  },
  destroyed() {
    storeDispatcher("mobileHeaderTitleCount", null);
  },
  methods: {
    pathOr,
    includes,
    checkPermission,
    generateQueryString,
    storeDispatcher,
    handleOpenMovingButtons() {
      this.isMovingButtons = true;
    },
    handleCloseMovingButtons() {
      this.isMovingButtons = false;
    },
    handleBackButton() {
      this.$emit("handleBackButton");
    },
    closeSearchText() {
      this.showSearchTxt = false;
    },
    eraseSearchText(fetchingData = true) {
      // remove text search data
      if (this.form.searchTxt) {
        this.form.searchTxt = null;
        if (fetchingData) {
          this.fetchData();
        }
      }
    },
    handleSubmitSearchText(key, value, searchKey) {
      // call of input mixin
      this.input(key, value);
      this.searchKey = searchKey;
      if (!value) {
        this.removeFilter(searchKey);
        return;
      }

      // add search key and search value to params
      this.params[searchKey] = value;

      // reset page
      this.params.page = 1;

      // fetching data with searchKey
      this.fetchData();
    },
    handleInputSearchTxt(params) {
      const searchTxt = pathOr(null, ["search_txt"], params);
      if (searchTxt) {
        this.input("searchTxt", searchTxt);
      }
    },
    showFilter() {
      this.filterVisible = !this.filterVisible;
    },
    closeFilter() {
      this.parentCurrentFilterKey = null;
      this.filterVisible = false;
    },
    async submitFilter(params) {
      try {
        this.filterLoading = true;
        this.params = { ...params, page: 1 };
        await this.fetchData();
        // get filter cache data for get and use standard filter params
        await this.getFilterCacheData(this.handleInputSearchTxt);
        this.closeFilter();
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.filterLoading = false;
      }
    },
    async deleteFilters(callback) {
      // delete and reset all filters params
      try {
        await this.deleteFilterCacheData();
        this.params = { ...this.defaultParams };
        this.closeFilter();
        callback();
        this.fetchData();
      } catch (error) {
        this.$generateError(error);
      }
    },
    removeParam(paramKey) {
      // delete a filter of history
      const newParams = {};
      for (const [key, value] of Object.entries(this.params)) {
        if (key !== paramKey) {
          newParams[key] = value;
        }
      }
      this.params = { ...newParams, ...this.defaultParams };
    },
    async removeFilter(filterKey) {
      this.closeFilter();
      this.removeParam(filterKey);
      await this.fetchData();
      this.getFilterCacheData(this.handleInputSearchTxt);
    },
    generateUrlWithParams() {
      // crete query string of params and merged to url
      const params = { ...this.extraParams, ...this.params };
      let output = this.url;
      const paramsKeys = Object.keys(params);
      if (paramsKeys.length) {
        output = `${output}?`;

        for (const [key, item] of Object.entries(params)) {
          let type = pathOr(null, ["type"], item);
          let value = pathOr(null, ["value"], item);
          const actionType = pathOr(null, ["actionType", "id"], value);

          // set filter type and value when get filter cache data
          if (Array.isArray(item)) {
            type = "filter_cache_array";
            value = item;
          }

          // generate query string by filter type
          if (includes(type, ["checkbox_multiselect", "filter_cache_array"])) {
            for (const valueItem of value) {
              output = `${output}${key}[]=${valueItem}&`;
            }
          } else if (includes(type, ["checkbox_singleselect", "text"])) {
            output = `${output}${key}=${value}&`;
          } else if (type === "number") {
            const num = pathOr(null, ["num"], value);
            const numOf = pathOr(null, ["numOf"], value);
            const numTo = pathOr(null, ["numTo"], value);
            output = `${output}${key}[]=${actionType}&`;
            if (num) {
              output = `${output}${key}[]=${num}&`;
            }
            if (numOf) {
              output = `${output}${key}[]=${numOf}&`;
            }
            if (numTo) {
              output = `${output}${key}[]=${numTo}&`;
            }
          } else if (includes(type, ["datetime", "date"])) {
            const date = pathOr(null, ["date"], value);
            const startAt = pathOr(null, ["startAt"], value);
            const endAt = pathOr(null, ["endAt"], value);
            output = `${output}${key}[]=${actionType}&`;
            if (date) {
              output = `${output}${key}[]=${date}&`;
            }
            if (startAt) {
              output = `${output}${key}[]=${startAt}&`;
            }
            if (endAt) {
              output = `${output}${key}[]=${endAt}&`;
            }
          } else {
            output = `${output}${key}=${item}&`;
          }
        }

        output = output.slice(0, -1); // remove last &
      }
      return output;
    },
    async getList() {
      // base method for get data list
      try {
        const url = this.generateUrlWithParams();
        const response = await this.$httpService.get(url);
        const data = pathOr(null, ["data", "data"], response);
        const result = pathOr([], ["result"], data);
        const meta = pathOr(null, ["data", "meta"], response);
        const oldOther = pathOr(null, ["data", "other"], response);
        const newOther = pathOr(null, ["data", "others"], response);
        this.others = this.isNewStructure ? newOther : oldOther;
        this.filters = pathOr([], ["data", "filters"], response);
        this.permissions = pathOr([], ["data", "permissions"], response);
        this.resource = pathOr(null, ["data", "resource"], response);
        this.exportable = pathOr(false, ["data", "exportable"], response);

        const pagingData = this.isNewStructure ? meta : data;
        const perPage = pathOr(this.defaultPerPage, ["per_page"], pagingData);
        this.paging = {
          total: pathOr(0, ["total"], pagingData),
          count: this.isNewStructure
            ? pathOr(0, ["last_page"], pagingData)
            : pathOr(0, ["count"], pagingData),
          totalPages: this.isNewStructure
            ? pathOr(1, ["last_page"], pagingData)
            : pathOr(1, ["total_pages"], pagingData),
          currentPage: pathOr(1, ["current_page"], pagingData),
          perPage,
        };
        this.$emit("getCount", this.paging.count);

        this.form.perPage = {
          id: perPage,
          caption: String(perPage),
        };
        this.$emit("getPermissions", this.permissions);
        this.$emit("getOtherData", this.others);
        this.$emit("getFilters", this.params);

        const items = this.isNewStructure ? data : result;
        return items;
      } catch (error) {
        this.$generateError(error);
      }
    },
    async fetchData() {
      // get first data
      if (this.loading) return;
      this.items = [];
      try {
        this.loading = true;
        const response = await this.getList();
        this.items = response || [];
        await storeDispatcher("mobileHeaderTitleCount", this.paging.total);
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.loading = false;
        this.firstLoaded = true;
      }
    },
    async loadMore() {
      // load more data and pushed to list
      if (!this.hasLazyLoadingPagination) return;
      if (this.loading || this.loadMoreLoading) return;
      if (this.paging.currentPage >= this.paging.totalPages) return;

      try {
        this.loadMoreLoading = true;
        this.params.page = this.paging.currentPage + 1;
        const newItems = await this.getList();
        this.items = [...this.items, ...newItems];
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.loadMoreLoading = false;
      }
    },
    async handleExportExcel() {
      // generate excel export data
      if (!this.hasExportExcel || !this.exportable || this.exportExcelLoading)
        return;

      try {
        this.exportExcelLoading = true;
        const filters = { ...this.params, ...this.otherExportExcelParams };
        const response = await exportExcel(this.resource, filters);
        const { message } = response;
        this.$notify.success(message);
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.exportExcelLoading = false;
      }
    },
    handleAddButton() {
      this.$emit("handleAddButton");
    },
    async reload() {
      this.params = { ...this.params, ...this.defaultParams };
      this.loading = true;
      await this.getFilterCacheData(this.handleInputSearchTxt);
      this.loading = false;
      this.fetchData();
    },
    handleOpenFilters(key) {
      this.parentCurrentFilterKey = key;
      this.filterVisible = true;
    },
  },
};
</script>
